/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2023
 * @flow
 */

import React from 'react';
import { Grid } from '@material-ui/core';
import { deepPurple } from '@material-ui/core/colors';
import {
  Category,
  ShoppingBasket,
  Receipt,
} from '@material-ui/icons';
import Card from '../Card';
import Table from '../Table';
import LineChart from '../LineChart';
import { getHourIn12HrFormat } from '../../utils';

const quantitySoldTableColumns = [
  {
    field: 'categoryName',
    headerName: 'Category',
    flex: 1,
    sortable: true,
    filter: 'agTextColumnFilter',
    footer: false,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    sortable: true,
    filter: 'agTextColumnFilter',
    footer: false,
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    flex: 0.5,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.quantity - nodeB.data.quantity;
    },
    footer: true,
  },
  {
    field: 'onPremiseCount',
    headerName: 'On Premise',
    flex: 0.5,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.onPremiseCount - nodeB.data.onPremiseCount;
    },
    footer: true,
  },
  {
    field: 'offPremiseCount',
    headerName: 'Off Premise',
    flex: 0.5,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.offPremiseCount - nodeB.data.offPremiseCount;
    },
    footer: true,
  },
];

const hourlyItemSaleTableColumns = [
  {
    field: 'formattedTime',
    headerName: 'Time',
    flex: 1,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.time - nodeB.data.time;
    },
    footer: false,
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    flex: 0.5,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.quantity - nodeB.data.quantity;
    },
    footer: true,
  },
  {
    field: 'onPremiseCount',
    headerName: 'On Premise',
    flex: 0.5,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.onPremiseCount - nodeB.data.onPremiseCount;
    },
    footer: true,
  },
  {
    field: 'offPremiseCount',
    headerName: 'Off Premise',
    flex: 0.5,
    sortable: true,
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
      return nodeA.data.offPremiseCount - nodeB.data.offPremiseCount;
    },
    footer: true,
  },
];

const Items = ({ items, commonData, hourlySales }) => {
  const { totalCheck, totalCategory, totalItem, totalGuest } = commonData;

  const averageItemsPerCheck = (totalItem && totalCheck) ? totalItem / totalCheck : 0;
  const averageItemsPerGuest = (totalItem && totalGuest) ? totalItem / totalGuest : 0;
  const cards = [
    { label: 'Categories', value: totalCategory, icon: Category },
    { label: 'Items', value: totalItem, icon: ShoppingBasket },
    { label: 'Average Items per check', value: Math.ceil(averageItemsPerCheck), icon: Receipt },
    { label: 'Average items per guest', value: Math.ceil(averageItemsPerGuest), icon: Receipt },
  ];

  //hourly sales
  const hourlySalesData = Array.from(hourlySales.values());
  const hourlyItemSalesLineChartData = {
    labels: hourlySalesData.map(it => getHourIn12HrFormat(it.hour)),
    datasets: [
      {
        label: 'Quantity',
        data: hourlySalesData.map(it => it.totalItem),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        borderWidth: 1,
      },
    ],
  };
  const hourlyItemSaleTableRows = hourlySalesData.map(it => ({
    id: it.hour,
    time: it.hour,
    formattedTime: getHourIn12HrFormat(it.hour),
    quantity: it.totalItem,
    onPremiseCount: it.onPremiseCount,
    offPremiseCount: it.offPremiseCount,
  }));

  return (
    <React.Fragment>
      <Grid container>
        {cards.map((card, i) =>
          <Grid item xs={12} sm={6} md={4} lg={3} key={`key - ${card.label}`}>
            <Card
              label={card.label}
              value={card.value}
              icon={card.icon}
              backgroundColor={deepPurple[500]}
            />
          </Grid>
        )}
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Table
            rowData={hourlyItemSaleTableRows}
            columnDefs={hourlyItemSaleTableColumns}
            title={'HOURLY ITEM SALES'}
            displayFooter
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <LineChart data={hourlyItemSalesLineChartData} title='HOURLY ITEM SALES' />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} sm={12} md={9} lg={9}>
          <Table
            rowData={Array.from(items.values()).sort((a, b) => b?.quantity - a?.quantity)}
            columnDefs={quantitySoldTableColumns}
            title={'QUANTITY SOLD'}
            displayFooter
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default Items;
